<template>
    <div class="flex flex-col gap-4 pb-8 max-w-4xl mx-auto">
        <div class="text-center pt-2">
            <h1 class="text-3xl text-theme-primary">Creación completa</h1>
            <p class="text-sm mt-1">
                Completa cada sección para configurar tu producto. Puedes editar
                cualquier sección confirmada antes de crear.
            </p>
        </div>
        <div
            ref="section-basics"
            class="rounded-xl overflow-hidden shadow-lg bg-theme-card"
        >
            <div
                class="flex items-center justify-between px-6 py-4"
                :class="[
                    isDone('basics') ? 'border-b border-theme-border' : '',
                    !isCurrent('basics') ? 'cursor-pointer' : ''
                ]"
                @click="!isCurrent('basics') ? editSection('basics') : null"
            >
                <div class="flex items-center gap-3">
                    <div
                        class="w-7 h-7 rounded-full flex items-center justify-center text-sm font-semibold bg-stack-primary text-white"
                    >
                        {{ sectionNumber("basics") }}
                    </div>
                    <h2 class="text-base font-semibold text-theme-base">
                        Datos básicos
                    </h2>
                </div>
            </div>

            <div
                v-if="isDone('basics')"
                class="px-6 pb-4 flex flex-wrap gap-x-6 gap-y-1 text-sm text-theme-secondary border-t border-theme-primary"
            >
                <span class="pt-3">
                    <span class="font-medium text-theme-base">Nombre:</span>
                    {{ basicData.name }}
                </span>
                <span class="pt-3">
                    <span class="font-medium text-theme-base"
                        >Precio de venta:</span
                    >
                    ${{ basicData.price.toLocaleString() }}
                </span>
                <span class="pt-3">
                    <span class="font-medium text-theme-base">SKU:</span>
                    {{ basicData.sku }}
                </span>
            </div>

            <div
                v-if="!isDone('basics') && isCurrent('basics')"
                class="px-8 pb-8 flex flex-col gap-6"
            >
                <div class="flex flex-col md:flex-row gap-5 items-start">
                    <div class="flex-1 w-full">
                        <custom-input
                            v-model="basicData.name"
                            label="Nombre del producto"
                            class="w-full"
                            :error="
                                !isValidName && validators.name
                                    ? 'El nombre es requerido'
                                    : ''
                            "
                            @blur="validators.name = true"
                        />
                    </div>
                    <div class="w-full md:w-64">
                        <barcode-scanner-input
                            v-model="basicData.sku"
                            enabled
                            check
                        />
                    </div>
                </div>

                <input-money-component
                    v-model="basicData.price"
                    enabled
                    label="Precio de venta"
                    :message-error="
                        !isValidPrice && validators.price
                            ? 'El precio de venta debe ser mayor a $0'
                            : ''
                    "
                    @blur="validators.price = true"
                />

                <div>
                    <p class="text-xs mb-2">Fotos del producto (opcional)</p>
                    <images-manage
                        id="complete-product-images"
                        v-model="basicData.images"
                        message="del producto"
                        default-size="squareImage"
                        :default-alt="
                            'Imagen de ' + (basicData.name || 'producto')
                        "
                        :default-filters="{ size: 'squareImage' }"
                        enabled
                    />
                </div>

                <div class="flex justify-center">
                    <app-button
                        size="large"
                        :disabled="!basicsValid"
                        @click="confirmSection('basics')"
                    >
                        Continuar →
                    </app-button>
                </div>
            </div>
        </div>

        <div
            v-if="isUnlocked('products')"
            ref="section-products"
            class="rounded-xl overflow-hidden shadow-lg bg-theme-card"
        >
            <div
                class="flex items-center justify-between px-6 py-4"
                :class="[
                    isDone('products') ? 'border-b border-theme-border' : '',
                    !isCurrent('products') ? 'cursor-pointer' : ''
                ]"
                @click="!isCurrent('products') ? editSection('products') : null"
            >
                <div class="flex items-center gap-3">
                    <div
                        class="w-7 h-7 rounded-full flex items-center justify-center text-sm font-semibold bg-stack-primary text-white"
                    >
                        {{ sectionNumber("products") }}
                    </div>
                    <h2 class="text-base font-semibold text-theme-base">
                        Productos
                    </h2>
                </div>
            </div>

            <div
                v-if="isDone('products')"
                class="px-6 pb-4 text-sm text-theme-secondary border-t border-theme-primary pt-3"
            >
                <span v-if="!variants.hasVariants"
                    >Sin variantes (producto único)</span
                >
                <span v-else>
                    {{ variants.rows.length }} variante(s) ·
                    {{ variantTypeLabel }}
                </span>
            </div>

            <div
                v-if="!isDone('products') && isCurrent('products')"
                class="flex flex-col gap-4 px-8 pb-8"
            >
                <p v-if="!canCreateVariants" class="text-sm text-theme-muted">
                    Tu plan crea productos únicos, sin variantes. Las tallas,
                    colores y demás variantes llegan con el plan Starter.
                    <button
                        v-if="canShowVariantsUpgrade"
                        type="button"
                        class="text-theme-primary underline"
                        @click="openVariantsUpgrade"
                    >
                        Ver planes
                    </button>
                </p>
                <div v-else class="rounded-xl flex flex-col gap-4 shadow-sm">
                    <div>
                        <h3 class="text-sm font-semibold text-theme-base">
                            ¿El producto tiene variantes?
                        </h3>
                        <p class="text-xs text-theme-secondary mt-1">
                            Tallas, colores, tamaños u otras variaciones
                        </p>
                    </div>
                    <div class="flex gap-4">
                        <button
                            class="flex-1 py-5 rounded-xl border-2 flex flex-col items-center gap-2 transition-colors"
                            :class="
                                variants.hasVariants === true
                                    ? 'border-stack-primary bg-stack-primary text-white'
                                    : 'border-theme-primary text-theme-secondary hover:border-stack-primary'
                            "
                            @click="variants.hasVariants = true"
                        >
                            <span class="text-3xl">🎨</span>
                            <span class="font-semibold text-sm">
                                Sí, tiene variantes
                            </span>
                        </button>
                        <button
                            class="flex-1 py-5 rounded-xl border-2 flex flex-col items-center gap-2 transition-colors"
                            :class="
                                variants.hasVariants === false
                                    ? 'border-stack-primary bg-stack-primary text-white'
                                    : 'border-theme-primary text-theme-secondary hover:border-stack-primary'
                            "
                            @click="variants.hasVariants = false"
                        >
                            <span class="text-3xl">📦</span>
                            <span class="font-semibold text-sm">
                                No, producto único
                            </span>
                        </button>
                    </div>
                </div>

                <div
                    v-if="variants.hasVariants === true"
                    class="flex flex-col gap-6"
                >
                    <div>
                        <h3
                            class="text-sm font-semibold text-theme-secondary uppercase tracking-widest mb-3"
                        >
                            Tipo de producto
                        </h3>
                        <product-type-plan-manager
                            :item-current="variants.productType"
                            @change="onSelectProductType"
                        />
                    </div>

                    <div v-if="showCustomRef">
                        <custom-input
                            v-model="variants.customLabel"
                            label="Nombre de la variante (ej: Material, Personaje)"
                        />
                    </div>

                    <div
                        v-if="variants.productType"
                        class="flex flex-col gap-4"
                    >
                        <div class="flex items-center justify-between">
                            <h3
                                class="text-sm font-semibold text-theme-secondary uppercase tracking-widest"
                            >
                                Variantes ({{ variants.rows.length }})
                            </h3>
                            <app-button
                                size="small"
                                border
                                @click="addVariantRow"
                            >
                                + Añadir variante
                            </app-button>
                        </div>

                        <div
                            v-for="(v, i) in variants.rows"
                            :key="i"
                            class="border border-theme-primary rounded-xl"
                        >
                            <div
                                class="flex items-center justify-between px-5 py-2 border-b border-theme-primary rounded-t-xl"
                            >
                                <span
                                    class="text-xs font-semibold text-theme-secondary uppercase tracking-wide"
                                >
                                    Variante {{ i + 1 }}
                                </span>
                                <button
                                    v-if="variants.rows.length > 1"
                                    class="text-theme-secondary hover:text-danger text-xl leading-none px-1"
                                    @click="removeVariantRow(i)"
                                >
                                    ×
                                </button>
                            </div>
                            <div
                                class="px-5 pt-7 pb-5 flex flex-wrap gap-x-6 gap-y-7"
                            >
                                <div
                                    v-if="showSizeField"
                                    class="flex-1 min-w-40"
                                >
                                    <custom-input
                                        v-model="v.size"
                                        :label="sizeLabel"
                                    />
                                </div>
                                <div
                                    v-if="showColorField"
                                    class="flex-1 min-w-48"
                                >
                                    <color-input
                                        :item-current="v.color"
                                        enabled
                                        @change="v.color = $event"
                                    />
                                </div>
                                <div class="flex-1 min-w-40">
                                    <input-money-component
                                        v-model="v.price"
                                        enabled
                                        label="Precio de venta"
                                        @input="v.customPrice = true"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div
                    v-if="isLastSection('products')"
                    class="flex flex-col sm:flex-row justify-center gap-3"
                >
                    <app-button
                        size="large"
                        color="success"
                        border
                        :loading="isLoading"
                        :disabled="!productsValid"
                        @click="submit(false)"
                    >
                        Crear producto
                    </app-button>
                    <app-button
                        size="large"
                        color="success"
                        :loading="isLoading"
                        :disabled="!productsValid"
                        @click="submit(true)"
                    >
                        Crear y publicar
                    </app-button>
                </div>
                <div v-else class="flex justify-center">
                    <app-button
                        size="large"
                        :disabled="!productsValid"
                        @click="confirmSection('products')"
                    >
                        Continuar →
                    </app-button>
                </div>
            </div>
        </div>

        <div
            v-if="isUnlocked('classification')"
            ref="section-classification"
            class="rounded-xl overflow-hidden shadow-lg bg-theme-card"
        >
            <div
                class="flex items-center justify-between px-6 py-4"
                :class="[
                    isDone('classification')
                        ? 'border-b border-theme-border'
                        : '',
                    !isCurrent('classification') ? 'cursor-pointer' : ''
                ]"
                @click="
                    !isCurrent('classification')
                        ? editSection('classification')
                        : null
                "
            >
                <div class="flex items-center gap-3">
                    <div
                        class="w-7 h-7 rounded-full flex items-center justify-center text-sm font-semibold bg-stack-primary text-white"
                    >
                        {{ sectionNumber("classification") }}
                    </div>
                    <h2 class="text-base font-semibold text-theme-base">
                        Clasificación
                    </h2>
                </div>
            </div>

            <div
                v-if="isDone('classification')"
                class="px-6 pb-4 text-sm text-theme-secondary border-t border-theme-primary flex flex-wrap gap-x-6 gap-y-1"
            >
                <span v-if="canListLines" class="pt-3">
                    <span class="font-medium text-theme-base">Categoría:</span>
                    {{
                        classification.hasLine && classification.subline.name
                            ? classification.subline.name
                            : "Sin categoría"
                    }}
                </span>
                <span v-if="canListBrands" class="pt-3">
                    <span class="font-medium text-theme-base">Marca:</span>
                    {{
                        classification.hasBrand && classification.brand.name
                            ? classification.brand.name
                            : "Sin marca"
                    }}
                </span>
            </div>

            <div
                v-if="!isDone('classification') && isCurrent('classification')"
                class="px-8 pb-8 flex flex-col gap-6"
            >
                <div
                    v-if="canListLines"
                    class="rounded-xl flex flex-col gap-4 shadow-sm"
                >
                    <h3 class="text-sm font-semibold text-theme-base">
                        ¿Quieres asociar el producto a una categoría?
                    </h3>
                    <div class="flex gap-4">
                        <button
                            class="flex-1 py-4 rounded-xl border-2 flex flex-col items-center gap-1 transition-colors"
                            :class="
                                classification.hasLine === true
                                    ? 'border-stack-primary bg-stack-primary text-white'
                                    : 'border-theme-primary text-theme-secondary hover:border-stack-primary'
                            "
                            @click="classification.hasLine = true"
                        >
                            <span class="text-2xl">📦</span>
                            <span class="font-semibold text-sm">Sí</span>
                        </button>
                        <button
                            class="flex-1 py-4 rounded-xl border-2 flex flex-col items-center gap-1 transition-colors"
                            :class="
                                classification.hasLine === false
                                    ? 'border-stack-primary bg-stack-primary text-white'
                                    : 'border-theme-primary text-theme-secondary hover:border-stack-primary'
                            "
                            @click="classification.hasLine = false"
                        >
                            <x-mark-icon class="w-6 h-6" />
                            <span class="font-semibold text-sm">No</span>
                        </button>
                    </div>
                    <subline-selector
                        v-if="classification.hasLine === true"
                        v-model="classification.subline"
                        :default-filters="{}"
                        enabled
                        fill
                        class="mt-2"
                    />
                </div>

                <div
                    v-if="canListBrands"
                    class="rounded-xl flex flex-col gap-4 shadow-sm"
                >
                    <h3 class="text-sm font-semibold text-theme-base">
                        ¿Quieres asociar el producto a una marca?
                    </h3>
                    <div class="flex gap-4">
                        <button
                            class="flex-1 py-4 rounded-xl border-2 flex flex-col items-center gap-1 transition-colors"
                            :class="
                                classification.hasBrand === true
                                    ? 'border-stack-primary bg-stack-primary text-white'
                                    : 'border-theme-primary text-theme-secondary hover:border-stack-primary'
                            "
                            @click="classification.hasBrand = true"
                        >
                            <span class="text-2xl">🏷️</span>
                            <span class="font-semibold text-sm">Sí</span>
                        </button>
                        <button
                            class="flex-1 py-4 rounded-xl border-2 flex flex-col items-center gap-1 transition-colors"
                            :class="
                                classification.hasBrand === false
                                    ? 'border-stack-primary bg-stack-primary text-white'
                                    : 'border-theme-primary text-theme-secondary hover:border-stack-primary'
                            "
                            @click="classification.hasBrand = false"
                        >
                            <x-mark-icon class="w-6 h-6" />
                            <span class="font-semibold text-sm">No</span>
                        </button>
                    </div>
                    <brand-selector
                        v-if="classification.hasBrand === true"
                        v-model="classification.brand"
                        enabled
                        fill
                        class="mt-2"
                    />
                </div>

                <div
                    v-if="isLastSection('classification')"
                    class="flex flex-col sm:flex-row justify-center gap-3"
                >
                    <app-button
                        size="large"
                        color="success"
                        border
                        :loading="isLoading"
                        :disabled="!classificationValid"
                        @click="submit(false)"
                    >
                        Crear producto
                    </app-button>
                    <app-button
                        size="large"
                        color="success"
                        :loading="isLoading"
                        :disabled="!classificationValid"
                        @click="submit(true)"
                    >
                        Crear y publicar
                    </app-button>
                </div>
                <div v-else class="flex justify-center">
                    <app-button
                        size="large"
                        :disabled="!classificationValid"
                        @click="confirmSection('classification')"
                    >
                        Continuar →
                    </app-button>
                </div>
            </div>
        </div>

        <div
            v-if="isUnlocked('inventory')"
            ref="section-inventory"
            class="rounded-xl overflow-hidden shadow-lg bg-theme-card"
        >
            <div
                class="flex items-center justify-between px-6 py-4"
                :class="[
                    isDone('inventory') ? 'border-b border-theme-border' : '',
                    !isCurrent('inventory') ? 'cursor-pointer' : ''
                ]"
                @click="
                    !isCurrent('inventory') ? editSection('inventory') : null
                "
            >
                <div class="flex items-center gap-3">
                    <div
                        class="w-7 h-7 rounded-full flex items-center justify-center text-sm font-semibold bg-stack-primary text-white"
                    >
                        {{ sectionNumber("inventory") }}
                    </div>
                    <h2 class="text-base font-semibold text-theme-base">
                        Inventario
                    </h2>
                </div>
            </div>

            <div
                v-if="isDone('inventory')"
                class="px-6 pb-4 text-sm text-theme-secondary border-t border-theme-primary pt-3"
            >
                <span v-if="!inventory.hasInventory"
                    >Sin inventario inicial</span
                >
                <span v-else-if="variants.hasVariants">
                    Inventario configurado por variante
                </span>
                <span v-else>
                    {{ inventory.stock }} unidades · costo ${{
                        inventory.cost.toLocaleString()
                    }}
                </span>
            </div>

            <div
                v-if="!isDone('inventory') && isCurrent('inventory')"
                class="px-8 pb-8 flex flex-col gap-6"
            >
                <div class="rounded-xl flex flex-col gap-4 shadow-sm">
                    <h3 class="text-sm font-semibold text-theme-base">
                        ¿Quieres añadir unidades al inventario?
                    </h3>
                    <div class="flex gap-4">
                        <button
                            class="flex-1 py-4 rounded-xl border-2 flex flex-col items-center gap-1 transition-colors"
                            :class="
                                inventory.hasInventory === true
                                    ? 'border-stack-primary bg-stack-primary text-white'
                                    : 'border-theme-primary text-theme-secondary hover:border-stack-primary'
                            "
                            @click="inventory.hasInventory = true"
                        >
                            <span class="text-2xl">📦</span>
                            <span class="font-semibold text-sm">Sí</span>
                        </button>
                        <button
                            class="flex-1 py-4 rounded-xl border-2 flex flex-col items-center gap-1 transition-colors"
                            :class="
                                inventory.hasInventory === false
                                    ? 'border-stack-primary bg-stack-primary text-white'
                                    : 'border-theme-primary text-theme-secondary hover:border-stack-primary'
                            "
                            @click="inventory.hasInventory = false"
                        >
                            <x-mark-icon class="w-6 h-6" />
                            <span class="font-semibold text-sm">No</span>
                        </button>
                    </div>

                    <template v-if="inventory.hasInventory === true">
                        <div
                            v-if="variants.hasVariants"
                            class="flex flex-col gap-3 mt-2"
                        >
                            <div
                                v-for="(v, i) in variants.rows"
                                :key="i"
                                class="border border-theme-primary rounded-lg px-4 py-3"
                            >
                                <p
                                    class="text-sm font-medium text-theme-base mb-2"
                                >
                                    {{ variantLabel(v) }}
                                </p>
                                <div class="flex flex-wrap gap-3">
                                    <div class="flex-1 min-w-32">
                                        <custom-input
                                            v-model.number="v.stock"
                                            type="number"
                                            min="0"
                                            label="Unidades"
                                            :error="
                                                v.stock < 0
                                                    ? 'No puede ser un número negativo'
                                                    : ''
                                            "
                                            @keydown.native="preventNegativeKey"
                                        />
                                    </div>
                                    <div class="flex-1 min-w-32">
                                        <input-money-component
                                            v-model="v.cost"
                                            enabled
                                            label="Costo"
                                        />
                                        <p
                                            v-if="costWarning(v.price, v.cost)"
                                            class="mt-1 text-xs text-theme-warning"
                                        >
                                            {{ costWarning(v.price, v.cost) }}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div v-else class="flex flex-wrap gap-4 mt-2">
                            <div class="flex-1 min-w-32">
                                <custom-input
                                    v-model.number="inventory.stock"
                                    type="number"
                                    min="0"
                                    label="Unidades"
                                    :error="
                                        inventory.stock < 0
                                            ? 'No puede ser un número negativo'
                                            : ''
                                    "
                                    @keydown.native="preventNegativeKey"
                                />
                            </div>
                            <div class="flex-1 min-w-32">
                                <input-money-component
                                    v-model="inventory.cost"
                                    enabled
                                    label="Costo"
                                />
                                <p
                                    v-if="
                                        costWarning(
                                            basicData.price,
                                            inventory.cost
                                        )
                                    "
                                    class="mt-1 text-xs text-theme-warning"
                                >
                                    {{
                                        costWarning(
                                            basicData.price,
                                            inventory.cost
                                        )
                                    }}
                                </p>
                            </div>
                        </div>
                    </template>
                </div>

                <div class="flex flex-col sm:flex-row justify-center gap-3">
                    <app-button
                        size="large"
                        color="success"
                        border
                        :loading="isLoading"
                        :disabled="!inventoryValid"
                        @click="submit(false)"
                    >
                        Crear producto
                    </app-button>
                    <app-button
                        size="large"
                        color="success"
                        :loading="isLoading"
                        :disabled="!inventoryValid"
                        @click="submit(true)"
                    >
                        Crear y publicar
                    </app-button>
                </div>
            </div>
        </div>

        <div
            v-if="allSectionsDone"
            class="flex flex-col sm:flex-row justify-center gap-3"
        >
            <app-button
                size="large"
                color="success"
                border
                :loading="isLoading"
                @click="submit(false)"
            >
                Crear producto
            </app-button>
            <app-button
                size="large"
                color="success"
                :loading="isLoading"
                @click="submit(true)"
            >
                Crear y publicar
            </app-button>
        </div>
    </div>
</template>

<script>
import planLimitError from "@/mixins/planLimitError";
import { mapActions, mapGetters, mapState } from "vuex";

import { canShowUpgrade } from "@/common/fields/plan-features";
import {
    PRODUCT_VARIANTS_FEATURE,
    PRODUCT_VARIANTS_PERMISSION,
    productTypeDescriptions,
    productTypesReferenceConfig
} from "@/common/fields/product-types";
import { preventNegativeKey } from "@/common/lib/number-input";
import { getCostWarning } from "@/common/lib/price-offer";
import { titleRegex } from "@/common/lib/regex";

import BrandSelector from "@/components/brands/BrandSelector.vue";
import XMarkIcon from "@/components/icons/XMarkIcon.vue";
import SublineSelector from "@/components/lines/SublineSelector.vue";
import ProductTypePlanManager from "@/components/products/ProductTypePlanManager.vue";
import BarcodeScannerInput from "@/components/utils/BarcodeScannerInput.vue";
import ColorInput from "@/components/utils/ColorInput.vue";
import CustomInput from "@/components/utils/CustomInput.vue";
import ImagesManage from "@/components/utils/ImagesManage.vue";
import InputMoneyComponent from "@/components/utils/InputMoneyComponent.vue";

export default {
    name: "ProductCompleteCreate",
    components: {
        ImagesManage,
        BrandSelector,
        SublineSelector,
        ProductTypePlanManager,
        InputMoneyComponent,
        ColorInput,
        BarcodeScannerInput,
        CustomInput,
        XMarkIcon
    },
    mixins: [planLimitError],
    layout: "app",
    data: () => ({
        sections: [],
        currentSection: "basics",
        completedSections: new Set(),
        unlockedSections: new Set(["basics"]),
        isLoading: false,
        validators: { name: false, price: false },
        basicData: {
            name: "",
            price: 1000,
            sku: "",
            images: []
        },
        variants: {
            hasVariants: null,
            productType: null,
            customLabel: "",
            rows: []
        },
        classification: {
            hasLine: null,
            hasBrand: null,
            subline: { name: "", slug: "" },
            brand: { name: "", slug: "" }
        },
        inventory: {
            hasInventory: null,
            stock: 1,
            cost: 0
        }
    }),
    computed: {
        ...mapState("commons", ["selectedMerchant"]),
        ...mapState("auth", ["permissions"]),
        ...mapGetters("control", ["backgroundColor"]),
        isValidName() {
            return titleRegex.test(this.basicData.name);
        },
        isValidPrice() {
            return this.basicData.price > 0;
        },
        basicsValid() {
            return this.isValidName && this.isValidPrice;
        },
        productsValid() {
            if (this.variants.hasVariants === null) return false;
            if (!this.variants.hasVariants) return true;
            if (!this.variants.productType) return false;
            if (this.variants.rows.length === 0) return false;
            return this.variants.rows.every((v) => {
                if (this.showSizeField && !v.size) return false;
                if (this.showColorField && !v.color?.color) return false;
                return true;
            });
        },
        classificationValid() {
            if (this.canListLines && this.classification.hasLine === null) {
                return false;
            }
            if (this.canListBrands && this.classification.hasBrand === null) {
                return false;
            }
            return true;
        },
        // Variantes desde Starter (feature de plan `productVariants`); Free
        // crea productos únicos.
        canCreateVariants() {
            return this.$ability.hasPermission(PRODUCT_VARIANTS_PERMISSION);
        },
        canShowVariantsUpgrade() {
            return canShowUpgrade(this.permissions, PRODUCT_VARIANTS_FEATURE);
        },
        canListLines() {
            return this.$ability.hasPermission("line:List");
        },
        canListBrands() {
            return this.$ability.hasPermission("brand:List");
        },
        canManageInventory() {
            return (
                !!this.selectedMerchant?.merchantId &&
                this.$ability.hasPermission("inventory:Create")
            );
        },
        inventoryValid() {
            if (this.inventory.hasInventory === null) return false;
            if (!this.inventory.hasInventory) return true;
            if (this.variants.hasVariants) {
                return this.variants.rows.every(
                    (v) => v.stock > 0 || v.cost >= 0
                );
            }
            return this.inventory.stock > 0;
        },
        allSectionsDone() {
            const last = this.sections[this.sections.length - 1];
            return !!last && this.completedSections.has(last.id);
        },
        currentTypeConfig() {
            return (
                productTypesReferenceConfig[this.variants.productType] || null
            );
        },
        showSizeField() {
            return this.currentTypeConfig?.showSize || false;
        },
        showColorField() {
            return this.currentTypeConfig?.showColor || false;
        },
        showCustomRef() {
            return this.currentTypeConfig?.showCustomReference || false;
        },
        sizeLabel() {
            return this.currentTypeConfig?.sizeLabel || "Talla / Tamaño";
        },
        colorLabel() {
            return this.currentTypeConfig?.colorLabel || "Color";
        },
        variantTypeLabel() {
            return (
                productTypeDescriptions[this.variants.productType]?.label || ""
            );
        }
    },
    watch: {
        "basicData.price"(newPrice) {
            this.variants.rows.forEach((row) => {
                if (!row.customPrice) row.price = newPrice;
            });
        }
    },
    created() {
        if (!this.canCreateVariants) this.variants.hasVariants = false;
        if (!this.canListLines) this.classification.hasLine = false;
        if (!this.canListBrands) this.classification.hasBrand = false;
    },
    async mounted() {
        const verifyPagePermission =
            this.$ability.verifyPagePermission.bind(this);
        await verifyPagePermission("product:Record", "/app/products");
        this.buildSections();
    },
    methods: {
        preventNegativeKey,
        costWarning(salePrice, cost) {
            return getCostWarning({ salePrice, cost, offerActive: false });
        },
        ...mapActions("products", ["recordProduct", "publishProduct"]),
        ...mapActions("platformSubscriptions", ["openPlanUpgrade"]),
        openVariantsUpgrade() {
            this.openPlanUpgrade({
                kind: "feature",
                feature: PRODUCT_VARIANTS_FEATURE
            });
        },
        isDone(id) {
            return this.completedSections.has(id);
        },
        buildSections() {
            const list = [
                { id: "basics", title: "Datos básicos" },
                { id: "products", title: "Productos" }
            ];
            if (this.canListLines || this.canListBrands) {
                list.push({ id: "classification", title: "Clasificación" });
            }
            if (this.canManageInventory) {
                list.push({ id: "inventory", title: "Inventario" });
            }
            this.sections = [...list];
        },
        isCurrent(id) {
            return this.currentSection === id;
        },
        isUnlocked(id) {
            return this.unlockedSections.has(id);
        },
        isLastSection(id) {
            const last = this.sections[this.sections.length - 1];
            return last?.id === id;
        },
        sectionNumber(id) {
            return this.sections.findIndex((s) => s.id === id) + 1;
        },
        nextSectionId(id) {
            const index = this.sections.findIndex((s) => s.id === id);
            return this.sections[index + 1]?.id || null;
        },
        confirmSection(id) {
            this.completedSections = new Set([...this.completedSections, id]);
            const next = this.nextSectionId(id);
            if (next) {
                this.currentSection = next;
                this.unlockedSections = new Set([
                    ...this.unlockedSections,
                    next
                ]);
                this.scrollToSection(next);
            }
        },
        editSection(id) {
            const updated = new Set(this.completedSections);
            updated.delete(id);
            this.completedSections = updated;
            this.currentSection = id;
            this.scrollToSection(id);
        },
        scrollToSection(id) {
            this.$nextTick(() => {
                this.$refs[`section-${id}`]?.scrollIntoView({
                    behavior: "smooth",
                    block: "start"
                });
            });
        },
        onSelectProductType(type) {
            this.variants.productType = type;
            this.variants.rows = [];
            this.addVariantRow();
        },
        addVariantRow() {
            const firstColor = this.variants.rows[0]?.color || {
                color: "blanco",
                colorRef: "#fff"
            };
            this.variants.rows.push({
                size: "",
                color: { ...firstColor },
                price: this.basicData.price,
                customPrice: false,
                stock: 1,
                cost: 0
            });
        },
        removeVariantRow(index) {
            this.variants.rows.splice(index, 1);
        },
        variantLabel(v) {
            return (
                [v.size, v.color?.color].filter(Boolean).join(" / ") ||
                "Variante"
            );
        },
        buildPayload(active = false) {
            const payload = {
                name: this.basicData.name,
                price: this.basicData.price,
                active,
                sku: this.basicData.sku || undefined,
                type:
                    this.variants.hasVariants && this.variants.productType
                        ? this.variants.productType
                        : "single"
            };

            if (this.basicData.images.length) {
                payload.images = this.basicData.images.map((img) => ({
                    id: img.id,
                    sequence: img.sequence.sequence
                }));
            }

            if (
                this.classification.hasLine &&
                this.classification.subline.slug
            ) {
                payload.lineSlug = this.classification.subline.slug;
            }

            if (
                this.classification.hasBrand &&
                this.classification.brand.slug
            ) {
                payload.brandSlug = this.classification.brand.slug;
            }

            if (
                this.inventory.hasInventory &&
                this.selectedMerchant?.merchantId
            ) {
                payload.merchantId = this.selectedMerchant.merchantId;
                payload.merchantType = this.selectedMerchant.merchantType;
            }

            if (this.variants.hasVariants && this.variants.rows.length) {
                payload.customReferenceTitle = this.showCustomRef
                    ? this.variants.customLabel
                    : "";

                payload.references = this.variants.rows.map((v) => {
                    const ref = {
                        price: v.price,
                        size: this.showSizeField
                            ? v.size || undefined
                            : undefined,
                        color: this.showColorField
                            ? v.color?.color || undefined
                            : undefined,
                        colorRef: this.showColorField
                            ? v.color?.colorRef || undefined
                            : undefined
                    };
                    if (this.inventory.hasInventory) {
                        ref.stock = v.stock;
                        ref.cost = v.cost;
                    }
                    return ref;
                });
            } else if (
                this.inventory.hasInventory &&
                !this.variants.hasVariants
            ) {
                payload.references = [
                    {
                        stock: this.inventory.stock,
                        cost: this.inventory.cost
                    }
                ];
            }

            return payload;
        },
        async submit(andPublish = false) {
            this.isLoading = true;
            this.$loading.show();

            try {
                const result = await this.recordProduct(
                    this.buildPayload(andPublish)
                );
                this.$loading.hide();
                this.$sound.play();

                const dialogResult = await this.$swal.fire({
                    background: this.backgroundColor,
                    title: andPublish
                        ? "Producto creado y publicado"
                        : "Producto creado",
                    html: `<div class="text-left">
                        <p><strong>ID:</strong> ${result.id}</p>
                        <p><strong>SKU:</strong> ${result.sku}</p>
                        <br/>
                        <p>¿Qué deseas hacer ahora?</p>
                    </div>`,
                    icon: "success",
                    showDenyButton: true,
                    showCloseButton: true,
                    showCancelButton: true,
                    confirmButtonText: "Ver detalle",
                    denyButtonText: "Ir a lista",
                    cancelButtonText: "Nuevo producto",
                    confirmButtonColor: "#02672D",
                    cancelButtonColor: "#253C99",
                    denyButtonColor: "#5C5CFF",
                    allowOutsideClick: false
                });

                if (dialogResult.isConfirmed) {
                    this.$router.push(`/app/products/detail/${result.id}`);
                } else if (dialogResult.isDenied) {
                    this.$router.push("/app/products/");
                } else {
                    this.resetForm();
                }
                this.isLoading = false;
                this.$loading.hide();
            } catch (error) {
                this.isLoading = false;
                this.$loading.hide();
                if (this.openPlanUpgradeForLimit(error)) return;
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        resetForm() {
            this.currentSection = "basics";
            this.completedSections = new Set();
            this.unlockedSections = new Set(["basics"]);
            this.validators = { name: false, price: false };
            this.basicData = { name: "", price: 1000, sku: "", images: [] };
            this.variants = {
                hasVariants: this.canCreateVariants ? null : false,
                productType: null,
                customLabel: "",
                rows: []
            };
            this.classification = {
                hasLine: this.canListLines ? null : false,
                hasBrand: this.canListBrands ? null : false,
                subline: { name: "", slug: "" },
                brand: { name: "", slug: "" }
            };
            this.inventory = { hasInventory: null, stock: 1, cost: 0 };
        }
    }
};
</script>
